import React, { useEffect, useState } from 'react';
import { useLocation } from '@reach/router';
import Header from '@/components/header';
import Footer from '@/components/footer';
import BackToTop from '@/components/utility/BackToTop';
// import { KeyDatesModal } from '@/components/KeyDates';
import { useSiteMetadata } from '@/hooks/use-site-metadata';
import Sidebar from '@/components/sidebar';
import config from '@/utils/config';
import { Helmet } from 'react-helmet';

export default function Layout({ children }) {
  const location = useLocation();
  const { siteUrl } = useSiteMetadata();
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
    const currentDomain = window.location.origin;

    if (currentDomain === siteUrl) {
      document.documentElement.lang = 'en';
    }
  }, [siteUrl]);

  useEffect(() => {
    // Adding a temporary class for positioning and setting the body ID based on the pathname
    document.body.classList.add('!mt-0');
    const pathname = location.pathname.replace(/\//g, '-').replace(/^-|-$/g, '');
    document.body.id = pathname ? pathname : 'home';

    const observerCallback = (mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1) {
              const elements = node.querySelectorAll('[data-radix-menubar-content]');
              elements.forEach(element => {
                if (element.parentElement) {
                  element.parentElement.classList.add('has-radix-menubar-content');
                }
              });
            }
          });
        }
      }
    };

    // Create and observe mutations on a narrower scope if possible
    const observer = new MutationObserver(observerCallback);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      document.body.classList.remove('!mt-0');
      document.body.removeAttribute('id');
    };
  }, [location.pathname]);

  // Configuration settings for the sidebar and key dates modal
  const sidebarEnabled = config.sidebar.enabled;
  const customPages = config.sidebar.customPages || [];
  const keydatesPopout = config.keydatesPopout || { enabled: false, customPages: [] };

  const shouldShowSidebar = sidebarEnabled && (customPages.includes(location.pathname) || customPages.length === 0);
  const shouldShowKeyDatesModal = keydatesPopout.enabled && keydatesPopout.customPages.includes(location.pathname);

  const removeDefaultPadding = ["/", "/workshop-venue/"];
  const shouldRemovePadding = removeDefaultPadding.includes(location.pathname);

  const sidebarClass = shouldShowSidebar ? 'lg:w-9/12' : 'w-full';
  const paddingClass = shouldRemovePadding ? '' : 'py-12 md:py-16 flex flex-col gap-12';

  const analyticsScript = `
          var _paq = window._paq = window._paq || [];
          _paq.push(['trackPageView']);
          _paq.push(['enableLinkTracking']);
          (function() {
              var u="//analytics.icmsaust.com.au/";
              _paq.push(['setTrackerUrl', u+'matomo.php']);
              _paq.push(['setSiteId', '15']);
              var d=document, g=d.createElement('script'), s=d.getElementsByTagName('script')[0];
              g.async=true; g.src=u+'matomo.js'; s.parentNode.insertBefore(g,s);
          })();
      `

  return (
    <>
      <Helmet>
          <script type="text/javascript">{analyticsScript}</script>
      </Helmet>
      <Header />
      <main className={`flex ${shouldShowSidebar ? 'flex-col lg:flex-row container mx-auto p-0' : 'flex-col'}`}>
        <div className={`w-full ${sidebarClass} ${paddingClass}`}>
          {children}
        </div>
        {shouldShowSidebar && (
          <div className="w-full lg:w-3/12 bg-gradient-to-b from-slate-50 to-slate-200 py-12 md:py-16">
            <Sidebar />
          </div>
        )}
      </main>
      <Footer />
      {isClient && <BackToTop />}
      {/* {isClient && shouldShowKeyDatesModal && <KeyDatesModal />} */}
    </>
  );
}
